import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Copy, Check, ShieldAlert, Tag, Plus, Archive, X } from "lucide-react";
import * as Api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function PromoCodesPage() {
  const { user, loading: authLoading } = useAuth();
  const [staffOk, setStaffOk] = useState(null); // null = checking, true/false resolved
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [creatorOpen, setCreatorOpen] = useState(false);

  async function loadCodes() {
    try {
      const d = await Api.listPromoCodes();
      setCodes(d.codes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh codes");
    }
  }

  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const f = await Api.getStaffFlag();
        if (cancelled) return;
        setStaffOk(f.is_staff);
        if (f.is_staff) {
          const d = await Api.listPromoCodes();
          if (!cancelled) setCodes(d.codes || []);
        }
      } catch (e) {
        if (!cancelled) {
          setStaffOk(false);
          toast.error(e?.response?.data?.detail || "Could not load promo codes");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user]);

  function copy(code) {
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(code);
      toast.success(`Copied "${code}" to clipboard`);
      setTimeout(() => setCopied(c => (c === code ? null : c)), 2000);
    });
  }

  async function archive(c) {
    if (!window.confirm(`Archive "${c.code}"? Anyone trying to use this code at checkout will get rejected.`)) return;
    try {
      await Api.archivePromoCode(c.id);
      toast.success(`Archived "${c.code}"`);
      loadCodes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    }
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#F26522] animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (staffOk === false) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-[#111111] border-2 border-[#F26522] p-8 flex items-start gap-4" data-testid="promo-staff-denied">
          <ShieldAlert className="w-8 h-8 text-[#F26522] shrink-0" />
          <div>
            <h2 className="font-heading font-black text-2xl text-white uppercase tracking-tight">Staff Only</h2>
            <p className="text-white/70 text-sm mt-2">
              This page is restricted to Poseidon Water Products staff. Promo codes are never exposed to dealers or technicians.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="promo-codes-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-block bg-[#F26522] text-black px-3 py-1 font-mono text-[10px] tracking-[0.25em] uppercase mb-3">Internal</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight leading-none">Promo Codes</h1>
          <p className="text-white/60 text-sm mt-3 max-w-2xl">
            Live from Stripe. Share codes individually with qualified prospects via email or DM —{" "}
            <strong className="text-[#F26522]">never paste them in public marketing</strong>.
          </p>
        </div>
        <button
          onClick={() => setCreatorOpen(o => !o)}
          data-testid="btn-new-promo"
          className="h-12 px-5 bg-[#F26522] text-black font-heading font-black text-sm tracking-wider uppercase flex items-center gap-2 hover:bg-[#FF7A2A] transition-colors"
        >
          {creatorOpen ? <><X className="w-4 h-4" /> Cancel</> : <><Plus className="w-4 h-4" /> New Code</>}
        </button>
      </div>

      {creatorOpen && (
        <CreatePromoForm
          onCancel={() => setCreatorOpen(false)}
          onCreated={() => { setCreatorOpen(false); loadCodes(); }}
        />
      )}

      {codes.length === 0 ? (
        <div className="bg-[#111111] border-2 border-white/10 p-8 text-white/60 text-sm" data-testid="promo-empty">
          No active promotion codes in Stripe right now.
        </div>
      ) : (
        <div className="space-y-3" data-testid="promo-codes-list">
          {codes.map(c => (
            <div key={c.id} className="bg-[#111111] border-2 border-white/10 hover:border-[#F26522]/50 transition-colors p-5" data-testid={`promo-row-${c.code}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <Tag className="w-5 h-5 text-[#F26522] shrink-0" />
                  <div className="min-w-0">
                    <div className="font-mono text-xl font-bold text-white tracking-wider truncate" data-testid={`promo-code-value-${c.code}`}>
                      {c.code}
                    </div>
                    <div className="text-white/60 text-xs mt-1">{c.name}</div>
                    {c.issued_to && (
                      <div className="text-[#F26522] text-[11px] font-mono tracking-wider uppercase mt-1.5 truncate" data-testid={`promo-issued-${c.code}`}>
                        Issued to: {c.issued_to}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copy(c.code)}
                    data-testid={`btn-copy-${c.code}`}
                    className="h-10 px-4 border-2 border-white/15 hover:border-[#F26522] hover:text-[#F26522] text-white font-mono text-[11px] tracking-wider uppercase flex items-center gap-2 transition-colors"
                  >
                    {copied === c.code ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
                  </button>
                  <button
                    onClick={() => archive(c)}
                    data-testid={`btn-archive-${c.code}`}
                    className="h-10 px-4 border-2 border-white/15 hover:border-[#F26522] hover:text-[#F26522] text-white/70 font-mono text-[11px] tracking-wider uppercase flex items-center gap-2 transition-colors"
                    title="Deactivate this code in Stripe"
                  >
                    <Archive className="w-4 h-4" /> Archive
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/10">
                <Stat label="Discount" value={c.discount} accent />
                <Stat label="Duration" value={c.duration} />
                <Stat label="Redeemed" value={String(c.times_redeemed ?? 0)} />
                <Stat label="Max" value={c.max_redemptions ? String(c.max_redemptions) : "—"} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 bg-black border border-white/10 p-5 text-white/50 text-xs leading-relaxed">
        Archiving a code here deactivates it in Stripe immediately. The audit trail (redemptions, coupon) stays in your Stripe Dashboard under Products → Coupons.
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">{label}</div>
      <div className={`font-mono text-sm mt-1 ${accent ? "text-[#F26522] font-bold" : "text-white"}`}>{value}</div>
    </div>
  );
}

function CreatePromoForm({ onCancel, onCreated }) {
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(100);
  const [duration, setDuration] = useState("once");
  const [months, setMonths] = useState(3);
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [firstTime, setFirstTime] = useState(false);
  const [name, setName] = useState("");
  const [issuedTo, setIssuedTo] = useState("");
  const [busy, setBusy] = useState(false);

  const codeOk = /^[A-Za-z0-9_-]{3,64}$/.test(code);
  const pctOk = percent >= 1 && percent <= 100;
  const monthsOk = duration !== "repeating" || (months >= 1 && months <= 60);
  const canSubmit = codeOk && pctOk && monthsOk && !busy;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = {
        code,
        percent_off: Number(percent),
        duration,
        first_time_transaction: firstTime,
      };
      if (duration === "repeating") payload.duration_in_months = Number(months);
      if (maxRedemptions) payload.max_redemptions = Number(maxRedemptions);
      if (name.trim()) payload.name = name.trim();
      if (issuedTo.trim()) payload.issued_to = issuedTo.trim();
      const r = await Api.createPromoCode(payload);
      toast.success(`Created "${r.code}"`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create promo code");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="bg-[#111111] border-2 border-[#F26522] p-6 mb-6 space-y-5" data-testid="promo-create-form">
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#F26522]">New Promotion Code</div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Input
          label="Code (what customers type)"
          value={code}
          onChange={setCode}
          placeholder="e.g. BeauWater50"
          testId="promo-input-code"
          monospace
          autoFocus
        />
        <Input
          label="Internal name (optional)"
          value={name}
          onChange={setName}
          placeholder="e.g. Beau Water - 50% off 3mo"
          testId="promo-input-name"
        />
      </div>

      <Input
        label="Issued to (optional — private, for attribution)"
        value={issuedTo}
        onChange={setIssuedTo}
        placeholder="e.g. Beau, ABC Plumbing — referral from Mike at trade show"
        testId="promo-input-issuedto"
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <Input
          label="Percent off"
          type="number"
          min="1" max="100"
          value={percent}
          onChange={setPercent}
          testId="promo-input-percent"
          suffix="%"
        />
        <div>
          <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-1">Duration</label>
          <select
            value={duration}
            onChange={e => setDuration(e.target.value)}
            data-testid="promo-select-duration"
            className="w-full h-12 bg-black border-2 border-white/15 px-3 text-white font-mono focus:border-[#F26522] focus:outline-none"
          >
            <option value="once">Once (single invoice)</option>
            <option value="repeating">Repeating (N months)</option>
            <option value="forever">Forever ⚠️</option>
          </select>
        </div>
        {duration === "repeating" && (
          <Input
            label="Number of months"
            type="number"
            min="1" max="60"
            value={months}
            onChange={setMonths}
            testId="promo-input-months"
          />
        )}
        {duration !== "repeating" && (
          <Input
            label="Max redemptions (optional)"
            type="number"
            min="1"
            value={maxRedemptions}
            onChange={setMaxRedemptions}
            placeholder="Unlimited"
            testId="promo-input-max"
          />
        )}
      </div>

      {duration === "repeating" && (
        <Input
          label="Max redemptions (optional)"
          type="number"
          min="1"
          value={maxRedemptions}
          onChange={setMaxRedemptions}
          placeholder="Unlimited"
          testId="promo-input-max"
        />
      )}

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={firstTime}
          onChange={e => setFirstTime(e.target.checked)}
          data-testid="promo-input-firsttime"
          className="w-5 h-5 accent-[#F26522]"
        />
        <span className="text-white/80 text-sm">First-time customers only</span>
      </label>

      {duration === "forever" && (
        <div className="bg-black border-l-4 border-[#F26522] p-3 text-[#F26522] text-xs font-mono uppercase tracking-wider">
          ⚠ Forever discounts persist on every renewal. Use carefully.
        </div>
      )}

      <div className="flex items-center gap-3 pt-3 border-t border-white/10">
        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="promo-create-submit"
          className="h-12 px-6 bg-[#F26522] text-black font-heading font-black text-sm tracking-wider uppercase flex items-center gap-2 hover:bg-[#FF7A2A] transition-colors disabled:opacity-50"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Create Code
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-12 px-5 border-2 border-white/15 text-white font-heading font-black text-sm tracking-wider uppercase hover:border-white transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function Input({ label, value, onChange, type = "text", testId, placeholder, monospace, suffix, autoFocus, min, max }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-1">{label}</label>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          autoFocus={autoFocus}
          min={min}
          max={max}
          className={`w-full h-12 bg-black border-2 border-white/15 px-3 text-white focus:border-[#F26522] focus:outline-none ${monospace ? "font-mono" : ""}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 font-mono text-sm">{suffix}</span>}
      </div>
    </div>
  );
}

